import { useMemo, useCallback } from 'react';
import { useAppAuthorizedUser } from '../../redux/hooks';
import { UserAuthorizationProps } from '../../config/interfaces';

export const useUserRolesRef = () => {
  const { userAuthorization } = useAppAuthorizedUser();

  const listOfRoles = useMemo(
    () => userAuthorization.map((auth: UserAuthorizationProps) => auth.role),
    [userAuthorization]
  );

  const hasRole = useCallback(
    (role: string) =>
      listOfRoles.includes(role) || listOfRoles.includes('enterpriseAdmin'),
    [listOfRoles]
  );

  const rolesEnabled = useMemo(
    () => ({
      isEnterpriseAdmin: hasRole('enterpriseAdmin'),
      isUserAdmin: hasRole('userAdmin'),
      isTemplateConfigurator: hasRole('templateConfigurator'),
      isPatientRounder: hasRole('patientRounder'),
      isEmployeeRounder: hasRole('employeeRounder'),
      isIlobRounder:
        hasRole('employeeRounder') &&
        !hasRole('enterpriseAdmin') &&
        (userAuthorization.find(
          (auth: UserAuthorizationProps) => auth.role === 'employeeRounder'
        )?.accessLevels.length ?? 0) === 0,
      isValidationRounder: hasRole('validationRounder'),
      isAidetValidationRounder: hasRole('aidetValidationRounder'),
      isBssrValidationRounder: hasRole('bssrValidationRounder'),
      isSafetyValidationRounder: hasRole('safetyValidationRounder'),
      isNonSupportRounder:
        hasRole('patientRounder') || hasRole('employeeRounder'),
      isAllNonSupportRounder:
        hasRole('patientRounder') && hasRole('employeeRounder'),
      isCNEdRounder: hasRole('cnedRounder'),
      isCSCRounder: hasRole('csrnRounder'),
      isSupportRounder: hasRole('cnedRounder') || hasRole('csrnRounder'),
      isAllSupportRounder: hasRole('cnedRounder') && hasRole('csrnRounder'),
      isAllValidationRounder:
        hasRole('validationRounder') &&
        hasRole('aidetValidationRounder') &&
        hasRole('bssrValidationRounder') &&
        hasRole('safetyValidationRounder'),
      isAnyValidationRounder:
        hasRole('validationRounder') ||
        hasRole('aidetValidationRounder') ||
        hasRole('bssrValidationRounder') ||
        hasRole('safetyValidationRounder'),
      hasMultipleValidationRoles:
        [
          hasRole('validationRounder'),
          hasRole('aidetValidationRounder'),
          hasRole('bssrValidationRounder'),
          hasRole('safetyValidationRounder')
        ].filter(Boolean).length > 1,
      isMultipleRounders: listOfRoles.length > 1,
      numOfRoles: listOfRoles.length,
      listOfRoles
    }),
    [listOfRoles, hasRole, userAuthorization]
  );

  return rolesEnabled;
};
